import { useEffect, useId, useState } from 'react';
import {
  DEFAULT_AFTER_HOURS_ENDS_AT,
  DEFAULT_DAY_ENDS_AT,
  ROUTING_MODES,
  WORK_PROOF_MODES,
  isDayEnd,
  type RoutingMode,
  type WorkProofMode,
} from '@/domain';
import { useLabSettings, useSaveLabSettings } from '@/application/usecases/useAdmin';
import { useSession } from '@/application/usecases/useSession';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { AppIconCard } from './AppIconCard';
import { GeofenceCard } from './GeofenceCard';
import usersStyles from './UsersScreen.module.css';
import styles from './SettingsScreen.module.css';

const COPY: Record<RoutingMode, { title: string; detail: string }> = {
  automatic: { title: strings.settings.automatic, detail: strings.settings.automaticDetail },
  manual: { title: strings.settings.manual, detail: strings.settings.manualDetail },
};

/** Proof of work, three ways. Section 2.5 of the request of 13 September 2026. */
const PROOF_COPY: Record<WorkProofMode, { title: string; detail: string }> = {
  off: { title: strings.proof.settingOff, detail: strings.proof.settingOffDetail },
  optional: { title: strings.proof.settingOptional, detail: strings.proof.settingOptionalDetail },
  mandatory: {
    title: strings.proof.settingMandatory,
    detail: strings.proof.settingMandatoryDetail,
  },
};

/** A 409 from the API, without reaching into infrastructure for its error type. */
function isConflict(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409;
}

/**
 * Settings. Section 2.3 of the system test handover.
 *
 * How a case leaves a station, and where station staff may sign in. A screen
 * of its own rather than another tab on Users: a manager restoring somebody's
 * access and a manager deciding how the floor routes work are not doing the
 * same job. The geofence moved here from the Users screen for the same reason.
 *
 * A choice saves as soon as it is made, as the access matrix does, and the
 * new mode reaches every job view through the session.
 *
 * As soon as it is made and confirmed: each choice, each time saved and each
 * Turn on or Turn off is asked about before it is sent, the request of
 * 5 October 2026 (third), section 2. A radio follows the server's value, so
 * Cancel leaves it where it was; a time that is not a time is refused before
 * anything is asked.
 */
export function SettingsScreen() {
  const { data: session } = useSession();
  const settings = useLabSettings();
  const save = useSaveLabSettings();
  const ask = useConfirmAction();
  const name = useId();
  const legendId = useId();
  const proofName = useId();
  const proofLegendId = useId();

  const current = settings.data ?? session?.settings;

  /*
   * The end of the day, request of 25 September 2026: a time typed rather than
   * a choice made, so it is saved on Save rather than on every keystroke. Read
   * tolerantly: an API from before the setting existed sends nothing, and the
   * card says so rather than offering a save that would change nothing.
   */
  const dayEndsAt: string | null | undefined = current?.dayEndsAt;
  const dayEndKnown = dayEndsAt !== undefined;
  const [dayEndDraft, setDayEndDraft] = useState('');
  const [dayEndError, setDayEndError] = useState<string | null>(null);
  const dayEndId = useId();
  useEffect(() => {
    setDayEndDraft(dayEndsAt ?? '');
  }, [dayEndsAt]);

  /*
   * When a clock started after the day end stops. Section 17 of the review of
   * 2 October 2026: an evening clock used to run to six the next evening.
   */
  const afterHoursEndsAt: string | null | undefined = current?.afterHoursEndsAt;
  const [afterHoursDraft, setAfterHoursDraft] = useState('');
  const [afterHoursError, setAfterHoursError] = useState<string | null>(null);
  const afterHoursId = useId();
  useEffect(() => {
    setAfterHoursDraft(afterHoursEndsAt ?? '');
  }, [afterHoursEndsAt]);

  function saveAfterHours(value: string | null) {
    if (current === undefined || save.isPending) return;
    if (value !== null && !isDayEnd(value)) {
      setAfterHoursError(strings.settings.dayEndInvalid);
      return;
    }
    setAfterHoursError(null);

    const next = {
      routing: current.routing,
      workProof: current.workProof,
      dayEndsAt: current.dayEndsAt ?? null,
      afterHoursEndsAt: value,
      rowVersion: current.rowVersion,
    };
    ask(
      strings.confirm.afterHours(value),
      () =>
        save.mutateAsync(next).then(
          (saved) =>
            toast(
              saved.afterHoursEndsAt === null
                ? strings.settings.afterHoursSavedOff
                : `${strings.settings.afterHoursSaved} ${saved.afterHoursEndsAt}`,
            ),
          report,
        ),
      { title: strings.settings.dayEndTitle },
    );
  }

  function report(error: unknown) {
    if (isConflict(error)) {
      toast(strings.settings.conflict);
      void settings.refetch();
      return;
    }
    toast(strings.settings.saveFailed);
  }

  function choose(routing: RoutingMode) {
    if (current === undefined || routing === current.routing || save.isPending) return;

    const next = {
      routing,
      workProof: current.workProof,
      dayEndsAt: current.dayEndsAt ?? null,
      afterHoursEndsAt: current.afterHoursEndsAt ?? null,
      rowVersion: current.rowVersion,
    };
    ask(
      strings.confirm.routing(COPY[routing].title),
      () =>
        save.mutateAsync(next).then(
          (saved) =>
            toast(
              `${strings.settings.saved} ${
                saved.routing === 'manual'
                  ? strings.settings.savedManual
                  : strings.settings.savedAutomatic
              }`,
            ),
          report,
        ),
      { title: strings.settings.routingTitle },
    );
  }

  /*
   * Whether a case at Final Check may be taken over. Section 9 of the request
   * of 5 October 2026: admin only, off unless switched on, each change logged
   * by the server. It rides on the session as the others do, so the next scan
   * anywhere in the lab follows it; the server enforces it either way.
   */
  const takeOverName = useId();
  const takeOverLegendId = useId();
  const takeOverAllowed = current?.takeOverAtFinalCheck === true;

  function chooseTakeOver(allowed: boolean) {
    if (current === undefined || allowed === takeOverAllowed || save.isPending) return;

    const next = {
      routing: current.routing,
      workProof: current.workProof,
      dayEndsAt: current.dayEndsAt ?? null,
      afterHoursEndsAt: current.afterHoursEndsAt ?? null,
      takeOverAtFinalCheck: allowed,
      rowVersion: current.rowVersion,
    };
    ask(
      strings.confirm.takeOverAtFinalCheck(allowed),
      () =>
        save.mutateAsync(next).then(
          (saved) =>
            toast(
              saved.takeOverAtFinalCheck === true
                ? strings.settings.takeOverSavedOn
                : strings.settings.takeOverSavedOff,
            ),
          report,
        ),
      { title: strings.settings.takeOverTitle },
    );
  }

  /*
   * Proof of work takes effect at once: it rides on the session, and every job
   * view refetches the session on mount, so the next Mark done anywhere in the
   * lab follows the new rule.
   */
  function chooseProof(workProof: WorkProofMode) {
    if (current === undefined || workProof === current.workProof || save.isPending) return;

    const next = {
      routing: current.routing,
      workProof,
      dayEndsAt: current.dayEndsAt ?? null,
      afterHoursEndsAt: current.afterHoursEndsAt ?? null,
      rowVersion: current.rowVersion,
    };
    ask(
      strings.confirm.workProof(PROOF_COPY[workProof].title),
      () =>
        save.mutateAsync(next).then(
          (saved) =>
            toast(
              `${strings.proof.settingSaved} ${PROOF_COPY[saved.workProof].title.toLowerCase()}`,
            ),
          report,
        ),
      { title: strings.proof.settingLabel },
    );
  }

  function saveDayEnd(value: string | null) {
    if (current === undefined || save.isPending) return;
    if (value !== null && !isDayEnd(value)) {
      setDayEndError(strings.settings.dayEndInvalid);
      return;
    }
    setDayEndError(null);

    const next = {
      routing: current.routing,
      workProof: current.workProof,
      dayEndsAt: value,
      afterHoursEndsAt: current.afterHoursEndsAt ?? null,
      rowVersion: current.rowVersion,
    };
    ask(
      strings.confirm.dayEnd(value),
      () =>
        save.mutateAsync(next).then(
          (saved) =>
            toast(
              saved.dayEndsAt === null || saved.dayEndsAt === undefined
                ? strings.settings.dayEndSavedOff
                : `${strings.settings.dayEndSaved} ${saved.dayEndsAt}`,
            ),
          report,
        ),
      { title: strings.settings.dayEndTitle },
    );
  }

  return (
    <div>
      <div className={usersStyles.head}>
        <span className={usersStyles.lbl}>{strings.settings.label}</span>
        <h2>{strings.settings.title}</h2>
        <span className={usersStyles.hint}>{strings.settings.hint}</span>
      </div>

      <section className={styles.card}>
        {current === undefined ? (
          <SkeletonRows rows={2} label={strings.settings.loading} />
        ) : (
          <fieldset className={styles.group} aria-describedby={legendId}>
            <legend className={styles.legend}>{strings.settings.routingTitle}</legend>
            <p id={legendId} className={styles.hint}>
              {strings.settings.routingHint}
            </p>

            <div className={styles.choices}>
              {ROUTING_MODES.map((mode) => (
                <label
                  key={mode}
                  className={`${styles.choice} ${mode === current.routing ? styles.on : ''}`}
                >
                  <input
                    type="radio"
                    name={name}
                    value={mode}
                    checked={mode === current.routing}
                    disabled={save.isPending}
                    onChange={() => choose(mode)}
                  />
                  <span className={styles.title}>{COPY[mode].title}</span>
                  <span className={styles.detail}>{COPY[mode].detail}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </section>

      <section className={styles.card}>
        {current === undefined ? (
          <SkeletonRows rows={3} label={strings.settings.loading} />
        ) : (
          <fieldset className={styles.group} aria-describedby={proofLegendId}>
            <legend className={styles.legend}>{strings.proof.settingLabel}</legend>
            <p id={proofLegendId} className={styles.hint}>
              {strings.proof.settingHint}
            </p>

            <div className={`${styles.choices} ${styles.three}`}>
              {WORK_PROOF_MODES.map((mode) => (
                <label
                  key={mode}
                  className={`${styles.choice} ${mode === current.workProof ? styles.on : ''}`}
                >
                  <input
                    type="radio"
                    name={proofName}
                    value={mode}
                    checked={mode === current.workProof}
                    disabled={save.isPending}
                    onChange={() => chooseProof(mode)}
                  />
                  <span className={styles.title}>{PROOF_COPY[mode].title}</span>
                  <span className={styles.detail}>{PROOF_COPY[mode].detail}</span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </section>

      <section className={styles.card}>
        {current === undefined ? (
          <SkeletonRows rows={2} label={strings.settings.loading} />
        ) : (
          <fieldset className={styles.group} aria-describedby={takeOverLegendId}>
            <legend className={styles.legend}>{strings.settings.takeOverTitle}</legend>
            <p id={takeOverLegendId} className={styles.hint}>
              {strings.settings.takeOverHint}
            </p>

            <div className={styles.choices}>
              {[false, true].map((allowed) => (
                <label
                  key={String(allowed)}
                  className={`${styles.choice} ${allowed === takeOverAllowed ? styles.on : ''}`}
                >
                  <input
                    type="radio"
                    name={takeOverName}
                    value={allowed ? 'on' : 'off'}
                    checked={allowed === takeOverAllowed}
                    disabled={save.isPending}
                    onChange={() => chooseTakeOver(allowed)}
                  />
                  <span className={styles.title}>
                    {allowed ? strings.settings.takeOverOn : strings.settings.takeOverOff}
                  </span>
                  <span className={styles.detail}>
                    {allowed ? strings.settings.takeOverOnDetail : strings.settings.takeOverOffDetail}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </section>

      <section className={styles.card}>
        {current === undefined ? (
          <SkeletonRows rows={2} label={strings.settings.loading} />
        ) : (
          <fieldset className={styles.group} aria-describedby={`${dayEndId}-hint`}>
            <legend className={styles.legend}>{strings.settings.dayEndTitle}</legend>
            <p id={`${dayEndId}-hint`} className={styles.hint}>
              {strings.settings.dayEndHint}
            </p>

            {!dayEndKnown ? (
              <p className={styles.hint}>{strings.settings.dayEndUnknown}</p>
            ) : dayEndsAt === null ? (
              <div className={styles.dayEnd}>
                <span className={styles.dayEndOff}>{strings.settings.dayEndOff}</span>
                <Button
                  variant="primary"
                  disabled={save.isPending}
                  onClick={() => saveDayEnd(DEFAULT_DAY_ENDS_AT)}
                >
                  {strings.settings.dayEndTurnOn}
                </Button>
              </div>
            ) : (
              <form
                className={styles.dayEnd}
                onSubmit={(event) => {
                  event.preventDefault();
                  saveDayEnd(dayEndDraft);
                }}
              >
                <label className={styles.dayEndLabel} htmlFor={dayEndId}>
                  {strings.settings.dayEndLabel}
                </label>
                <input
                  id={dayEndId}
                  className={styles.dayEndInput}
                  type="time"
                  value={dayEndDraft}
                  disabled={save.isPending}
                  onChange={(event) => {
                    setDayEndDraft(event.target.value);
                    setDayEndError(null);
                  }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={save.isPending || dayEndDraft === dayEndsAt}
                >
                  {strings.settings.dayEndSave}
                </Button>
                <Button variant="text" disabled={save.isPending} onClick={() => saveDayEnd(null)}>
                  {strings.settings.dayEndTurnOff}
                </Button>
                {dayEndError !== null && (
                  <p className={styles.dayEndError} role="alert">
                    {dayEndError}
                  </p>
                )}
              </form>
            )}

            {/* After hours: only meaningful while the day has an end. */}
            {dayEndKnown && dayEndsAt !== null && afterHoursEndsAt !== undefined && (
              <>
                <p id={`${afterHoursId}-hint`} className={styles.hint}>
                  {strings.settings.afterHoursHint}
                </p>
                {afterHoursEndsAt === null ? (
                  <div className={styles.dayEnd}>
                    <span className={styles.dayEndOff}>{strings.settings.afterHoursOff}</span>
                    <Button
                      variant="primary"
                      disabled={save.isPending}
                      onClick={() => saveAfterHours(DEFAULT_AFTER_HOURS_ENDS_AT)}
                    >
                      {strings.settings.afterHoursTurnOn}
                    </Button>
                  </div>
                ) : (
                  <form
                    className={styles.dayEnd}
                    onSubmit={(event) => {
                      event.preventDefault();
                      saveAfterHours(afterHoursDraft);
                    }}
                  >
                    <label className={styles.dayEndLabel} htmlFor={afterHoursId}>
                      {strings.settings.afterHoursLabel}
                    </label>
                    <input
                      id={afterHoursId}
                      className={styles.dayEndInput}
                      type="time"
                      value={afterHoursDraft}
                      aria-describedby={`${afterHoursId}-hint`}
                      disabled={save.isPending}
                      onChange={(event) => {
                        setAfterHoursDraft(event.target.value);
                        setAfterHoursError(null);
                      }}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={save.isPending || afterHoursDraft === afterHoursEndsAt}
                      aria-label={strings.settings.afterHoursSave}
                    >
                      {strings.settings.dayEndSave}
                    </Button>
                    <Button
                      variant="text"
                      disabled={save.isPending}
                      onClick={() => saveAfterHours(null)}
                      aria-label={strings.settings.afterHoursTurnOff}
                    >
                      {strings.settings.dayEndTurnOff}
                    </Button>
                    {afterHoursError !== null && (
                      <p className={styles.dayEndError} role="alert">
                        {afterHoursError}
                      </p>
                    )}
                  </form>
                )}
              </>
            )}
          </fieldset>
        )}
      </section>

      <section className={styles.location} aria-label={strings.settings.locationTitle}>
        <GeofenceCard />
      </section>

      {/*
        The installed app's icon and colours are read as well as changed by a
        holder of admin alone, so the card is neither asked for nor drawn for a
        reader of this screen who is not one. The lab's request of 11 October 2026.
      */}
      {session?.permissions.includes('admin') === true && <AppIconCard />}
    </div>
  );
}
